import { timestampToDate } from "@storytell/cp-client";
import { AssetLifecycleState } from "@storytell/cp-client/src/gen/assets/v3/asset_pb";
import { UIStatus } from "@storytell/streaming";
import type { AssetId } from "@storytell/types";
import { onCleanup } from "solid-js";
import { useFileUpload } from "../../../../lib/file-upload";
import { useWire } from "../../../../wire";
import { PromptBarMode, useAppUI } from "../../../layouts/minimal/AppUIContext";
import { usePromptBarContext } from "../PromptBarContext";

/**
 * Hook for managing drag and drop functionality
 * Full-screen dropzone that activates when files are dragged from outside the page
 */
export const useDragAndDrop = () => {
  const { state, actions } = useAppUI();
  const context = usePromptBarContext();
  const wire = useWire();
  const scope = wire.chat.scope;

  // Track if drag originated from within the page
  let dragStartedFromPage = false;

  const fileUpload = useFileUpload({
    getAuthToken: () => wire.auth.token(),
    onAssetCreated: (assetId, asset) => {
      const lifecycleState = (Object.entries(AssetLifecycleState).find(
        ([_, value]) => value === asset.data.lifecycleState,
      )?.[0] ||
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        "UNKNOWN") as any;

      wire.sync.assets.set(assetId as AssetId, {
        userId: wire.auth.user()?.id || "",
        fileName: asset.data.displayName,
        description: asset.data.summary,
        lifecycleState,
        deletedAt: asset.data.deletedAt ? timestampToDate(asset.data.deletedAt).toISOString() : undefined,
        deletedBy: asset.data.deletedBy,
        id: assetId as AssetId,
        displayName: asset.data.displayName,
        contentType: asset.data.contentType,
        tenantId: asset.data.tenantId,
        organizationId: asset.data.organizationId,
        workspaceId: asset.data.workspaceId,
        projectId: asset.data.projectId,
        createdBy: asset.data.auditMetadata?.created.userId || "",
        createdAt: asset.data.auditMetadata?.created.occurredAt
          ? timestampToDate(asset.data.auditMetadata.created.occurredAt).toISOString()
          : "",
        modifiedBy: asset.data.auditMetadata?.modified.userId || "",
        modifiedAt: asset.data.auditMetadata?.modified.occurredAt
          ? timestampToDate(asset.data.auditMetadata.modified.occurredAt).toISOString()
          : "",
        isArchived: false,
      });
      // Find and remove any temporary asset with the same filename
      const tempAsset = scope.uploadingAssets().find((a) => a.filename === asset.data.originalFilename);
      if (tempAsset) {
        scope.removeUploadingAsset(tempAsset.id);
      }

      wire.chat.scope.addAsset(assetId);
    },
  });

  const isDropzoneMode = () => state.promptBar.mode === PromptBarMode.DROPZONE;

  // Check if we're in submitted/chat mode (more aggressive dropzone activation)
  const isInSubmittedMode = () => {
    const streamingState = context.streaming;
    return streamingState?.uiStatus() === UIStatus.GENERATING || streamingState?.uiStatus() === UIStatus.DONE;
  };

  // Track when a drag starts from within the page
  const handleDragStart = (e: DragEvent) => {
    dragStartedFromPage = true;
  };

  // Reset the flag when drag ends
  const handleDragEnd = (e: DragEvent) => {
    dragStartedFromPage = false;
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    // Only activate dropzone if:
    // 1. Files are being dragged
    // 2. Drag did not originate from within the page (to avoid text selection triggering it)
    if (e.dataTransfer?.types.includes("Files") && !dragStartedFromPage) {
      actions.setPromptBarMode(PromptBarMode.DROPZONE);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    // Only hide dropzone when truly leaving the window
    if (e.clientX === 0 && e.clientY === 0) {
      actions.setPromptBarMode(PromptBarMode.NORMAL);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    // Keep dropzone active while dragging files
    if (e.dataTransfer?.types.includes("Files") && !dragStartedFromPage) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer?.files || []);

    if (files.length > 0) {
      // Switch to normal mode immediately to show prompt view with scope indicators
      actions.setPromptBarMode(PromptBarMode.NORMAL);

      // Create temporary asset entries for immediate UI feedback
      const tempAssets = files.map((file) => {
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        return { tempId, filename: file.name };
      });

      try {
        await fileUpload.uploadFiles(files, wire.auth.realm().projectId);

        // Clean up any remaining temporary assets (in case onAssetCreated didn't fire for some files)
        tempAssets.forEach(({ filename }) => {
          // Only remove if this temporary asset still exists and hasn't been replaced by a real asset
          const stillExists = scope.uploadingAssets().find((asset) => asset.filename === filename);
          if (stillExists) {
            scope.removeUploadingAsset(stillExists.id);
          }
        });
      } catch (error) {
        console.error("Failed to upload files:", error);
        // Update any remaining temporary assets to error state
        tempAssets.forEach(({ tempId }) => {
          const stillExists = scope.uploadingAssets().find((asset) => asset.id === tempId);
          if (stillExists) {
            scope.updateUploadingAssetStatus(tempId, "error");
          }
        });
      }
    } else {
      actions.setPromptBarMode(PromptBarMode.NORMAL);
    }

    // Reset the flag after drop
    dragStartedFromPage = false;
  };

  // Set up global drag listeners for full-screen dropzone
  if (typeof window !== "undefined") {
    // Track drag start from within the page
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("dragend", handleDragEnd);

    // Global drag/drop listeners
    document.addEventListener("dragenter", handleDragEnter);
    document.addEventListener("dragleave", handleDragLeave);
    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("drop", handleDrop);

    onCleanup(() => {
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("dragend", handleDragEnd);
      document.removeEventListener("dragenter", handleDragEnter);
      document.removeEventListener("dragleave", handleDragLeave);
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("drop", handleDrop);
    });
  }

  return {
    isDropzoneMode,
    isInSubmittedMode, // Export for debugging/testing
    fileUpload, // Export file upload state for UI components
  };
};
